// "A pagar": parcelas das compras a prazo e das contas fixas, o agenciamento
// das transportadoras e os acertos em que a empresa ficou devendo, separados
// em vencidas, próximos 7 dias e depois. "Paguei" registra o pagamento (sem
// sinal também, menos o do acerto, que passa pelo servidor).
import {
  aPagar, categorias, desfazerAgenciamentoPago, pagarAgenciamento, pagosDesde, parcelas, quitarAcerto, situacoesDeEnvio, veiculos,
} from '../dados.js';
import { separarAPagar, somarDias } from '../lib/despesas.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, plural } from '../lib/formato.js';
import { nestaTela } from '../rotas.js';
import { hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, carregando, confirmar, emblema, faixa, secao, vazio } from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { faixaDaConta } from './inicio.js';
import { NOME_FORMA, dialogoPagar, nomeDaDespesa } from './pagar.js';

// Totais para o cartão da tela inicial: { vencidas, semana, qtdVencidas, qtdSemana, tudo }.
export async function resumoAPagar() {
  const grupos = separarAPagar(await aPagar(), hojeDaEmpresa());
  return { ...grupos.totais, qtdVencidas: grupos.vencidas.length, qtdSemana: grupos.semana.length, qtd: grupos.vencidas.length + grupos.semana.length + grupos.depois.length };
}

export function telaAPagar() {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const hoje = hojeDaEmpresa();
    const [itens, pagos, cats, frota, envioParcelas, envioFretes] = await Promise.all([
      aPagar(), pagosDesde(somarDias(hoje, -30)), categorias.todas(), veiculos.listar(), situacoesDeEnvio('parcelas'), situacoesDeEnvio('fretes'),
    ]);
    const gravavel = podeGravar();
    const grupos = separarAPagar(itens, hoje);

    const executar = async (acao, sucesso) => {
      try {
        await acao();
        avisar(sucesso);
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    };

    const titulos = (item) => {
      if (item.tipo === 'acerto') {
        const a = item.acerto;
        return {
          nome: `Acerto nº ${a.numero}/${a.ano} · ${item.colaborador?.apelido || item.colaborador?.nome || 'colaborador'}`,
          linhas: [a.periodo_de && a.periodo_de !== a.data ? `Período: ${data(a.periodo_de)} a ${data(a.data)}` : null],
          href: `#/acertos/${a.id}`,
          rotulo: 'Abrir o acerto',
          envio: null,
        };
      }
      if (item.tipo === 'agenciamento') {
        return {
          nome: `Agenciamento · ${item.cliente?.nome ?? 'transportadora'}`,
          linhas: [`Carga de ${data(item.frete.data_carregamento)} · frete ${dinheiro(item.frete.valor_frete)}`],
          href: `#/cargas/${item.id}`,
          rotulo: 'Abrir a carga',
          envio: envioFretes.get(item.id),
        };
      }
      const cat = cats.find((c) => c.id === item.despesa.categoria_id);
      const veic = frota.find((v) => v.id === item.despesa.veiculo_id);
      return {
        nome: nomeDaDespesa(item.despesa, { categoria: cat, veiculo: veic }),
        linhas: [
          item.parcela.total > 1 ? `Parcela ${item.parcela.numero} de ${item.parcela.total}` : null,
          [item.despesa.fornecedor, !item.despesa.recorrencia_id && item.despesa.descricao].filter(Boolean).join(' · ') || null,
        ],
        href: `#/despesas/${item.despesa.id}?volta=${encodeURIComponent('/apagar')}`,
        rotulo: 'Abrir a despesa',
        envio: envioParcelas.get(item.id),
      };
    };

    const pagar = async (item) => {
      const t = titulos(item);
      if (item.tipo === 'acerto') {
        const r = await dialogoPagar({ titulo: 'Pagar o acerto', detalhe: `${t.nome}. Precisa de internet.`, valor: item.valor });
        if (r) executar(() => quitarAcerto(item.id, { como: 'paga', data: r.pago_em, forma: r.forma }), 'Pagamento registrado.');
        return;
      }
      if (item.tipo === 'agenciamento') {
        const r = await dialogoPagar({ titulo: 'Pagar o agenciamento', detalhe: t.nome, valor: item.valor, comForma: false });
        if (r) executar(() => pagarAgenciamento(item.id, r.pago_em), 'Pagamento registrado.');
        return;
      }
      const forma = item.despesa.forma === 'faturado' ? null : item.despesa.forma;
      const r = await dialogoPagar({ titulo: 'Registrar pagamento', detalhe: t.nome, valor: item.valor, vencimento: item.vencimento, formaPadrao: forma });
      if (r) executar(() => parcelas.pagar(item.id, r), 'Pagamento registrado.');
    };

    const desfazer = async (item) => {
      const ok = await confirmar({ titulo: 'Desfazer o pagamento?', texto: 'Ele volta para "A pagar".', sim: 'Desfazer' });
      if (!ok) return;
      executar(() => (item.tipo === 'agenciamento' ? desfazerAgenciamentoPago(item.id) : parcelas.reabrir(item.id)), 'Voltou para "A pagar".');
    };

    const cartao = (item, { pago = false } = {}) => {
      const t = titulos(item);
      const vencida = !pago && item.vencimento < hoje;
      return h('div', { class: `cartao conta${vencida ? ' vencida' : ''}` }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${dinheiro(item.valor)} · ${t.nome}`),
        h('span', { class: 'cartao-linha' }, pago
          ? `Pago em ${data(item.pago_em)}${item.parcela?.forma ? ` · ${NOME_FORMA[item.parcela.forma] ?? item.parcela.forma}` : ''}`
          : `${vencida ? 'Venceu em' : 'Vence em'} ${data(item.vencimento)}`),
        t.linhas.filter(Boolean).map((l) => h('span', { class: 'cartao-linha' }, l)),
        h('span', { class: 'cartao-emblemas' },
          pago ? emblema('Pago', 'ok') : vencida ? emblema('Vencida', 'perigo') : item.vencimento === hoje ? emblema('Vence hoje', 'alerta') : null,
          emblemaEnvio(t.envio)),
        h('div', { class: 'acoes-lado' },
          gravavel && (pago
            ? item.tipo !== 'acerto' && h('button', { class: 'botao texto', type: 'button', onClick: () => desfazer(item) }, 'Desfazer')
            : h('button', { class: 'botao', type: 'button', onClick: () => pagar(item) }, 'Paguei')),
          h('a', { class: 'botao secundario', href: t.href, 'aria-label': t.rotulo }, 'Abrir'))));
    };

    const grupo = (titulo, lista, total) => lista.length > 0 && secao(titulo, h('div', { class: 'lista' }, lista.map((item) => cartao(item))), {
      contador: `${plural(lista.length, 'conta')} · ${dinheiro(total)}`,
    });

    trocar(corpo,
      itens.length === 0
        ? vazio({
          simbolo: 'check',
          titulo: 'Nada a pagar',
          texto: 'Aqui aparecem as parcelas das compras a prazo, as contas fixas do mês e o agenciamento das transportadoras.',
          acao: h('a', { class: 'botao secundario', href: '#/contas-fixas' }, 'Ver contas fixas'),
        })
        : h('dl', { class: 'linha-dados resumo' },
          h('dt', null, 'Vencidas'), h('dd', { class: grupos.totais.vencidas > 0 ? 'perigo' : null }, dinheiro(grupos.totais.vencidas)),
          h('dt', null, 'Próximos 7 dias'), h('dd', null, dinheiro(grupos.totais.semana)),
          h('dt', null, 'Depois'), h('dd', null, dinheiro(grupos.totais.depois)),
          h('dt', null, 'Total em aberto'), h('dd', null, dinheiro(grupos.totais.tudo))),
      grupo('Vencidas', grupos.vencidas, grupos.totais.vencidas),
      grupo('Próximos 7 dias', grupos.semana, grupos.totais.semana),
      grupo('Depois', grupos.depois, grupos.totais.depois),
      pagos.length > 0 && h('details', { class: 'mais-detalhes' },
        h('summary', null, `Pagos nos últimos 30 dias (${pagos.length})`),
        h('div', { class: 'lista' }, pagos.map((item) => cartao(item, { pago: true })))),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'A pagar', voltar: '/inicio', conteudo: [faixaDaConta(), corpo] });
}
